import { Controller } from "@hotwired/stimulus"

require("activeadmin_quill_editor")

export default class extends Controller {
  static targets = [ "content", "textarea", "form", "toolbar" ]
  static values = {
    placeholder: String,
    readonly: Boolean
  }

  connect() {
    const quill = new Quill(this.contentTarget, {
      theme: 'snow',
      placeholder: this.placeholderValue || '',
      modules: {
        toolbar: this.toolbarTarget,
        keyboard: {
          bindings: {
            // Quill's default Tab binding inserts a tab character and
            // preventDefault's the key, so focus cannot leave the editor.
            tab: false
          }
        }
      },
    });

    // The <label> points at the hidden textarea. Name the contenteditable
    // Quill actually focuses so screen readers announce that label.
    const textareaId = this.textareaTarget.id
    if (textareaId) {
      const label = this.element.querySelector(`label[for="${CSS.escape(textareaId)}"]`)
      if (label) {
        if (!label.id) label.id = `${textareaId}-label`
        quill.root.setAttribute('aria-labelledby', label.id)
      }
    }

    // Quill sizes .ql-editor to its content. Flex the container so the
    // editor fills the min-height and the focus ring wraps the full box.
    this.contentTarget.classList.add('d-flex', 'flex-column')
    quill.root.classList.add('flex-grow-1')

    quill.on('text-change', (delta, oldDelta, source) => {
      this.textareaTarget.value = quill.container.firstChild.innerHTML
    })

    this.quill = quill;
    this.setEnabled(!this.readonlyValue);
    this.applyToolbarAccessibilityHacks(quill);

    const form = this.formElement();
    if (!form) return;

    // All of this nonsense is required because Quill insists on
    // wrapping ALL content in paragraphs, even empty content.
    // So an empty editor, when saved, would result in "<p></p>" being
    // saved to the database. This is not ideal.
    // This graphs the content, loops through the <p>'s and removes
    // them if they don't have any content.
    this.submitHandler = () => {
      if (quill.editor.isBlank()) {
        this.textareaTarget.value = '';
      } else {
        var ele = document.createElement('body');
        ele.innerHTML = this.textareaTarget.value;

        // if there is one empty paragraph, the textare should be treated as empty
        if(ele.querySelectorAll('p').length === 1) {
          [...ele.querySelectorAll('p')].forEach(para => {
            if (para.innerText.trim() === "") ele.removeChild(para);
          });
        }

        this.textareaTarget.value = ele.innerHTML;
      }
    }

    form.addEventListener('submit', this.submitHandler)
  }

  applyToolbarAccessibilityHacks(quill) {
    // Snow theme appends a link tooltip after init. "Enter link:" is only
    // CSS on the tooltip, so the URL input has no accessible name.
    const linkInput = quill.theme?.tooltip?.textbox
      || quill.container.querySelector('.ql-tooltip input[type="text"]');
    linkInput?.setAttribute('aria-label', 'Enter link');

    // The tooltip's links have no text of their own. Preview is empty until a
    // URL is chosen, and Edit/Remove only exist as CSS content.
    const tooltipRoot = quill.theme?.tooltip?.root
      || quill.container.querySelector('.ql-tooltip');
    if (tooltipRoot) {
      const previewLink = tooltipRoot.querySelector('a.ql-preview');
      const actionLink = tooltipRoot.querySelector('a.ql-action');
      const removeLink = tooltipRoot.querySelector('a.ql-remove');

      const labelTooltipLinks = () => {
        const editing = tooltipRoot.classList.contains('ql-editing');
        actionLink?.setAttribute('aria-label', editing ? 'Save link' : 'Edit link');
        removeLink?.setAttribute('aria-label', 'Remove link');

        if (!previewLink) return;

        const url = previewLink.textContent.trim();
        if (url) {
          previewLink.removeAttribute('aria-hidden');
          previewLink.removeAttribute('aria-label');
        } else {
          previewLink.setAttribute('aria-hidden', 'true');
          previewLink.setAttribute('aria-label', 'Link preview');
        }
      };

      labelTooltipLinks();
      this.tooltipLinkObserver = new MutationObserver(labelTooltipLinks);
      this.tooltipLinkObserver.observe(tooltipRoot, {
        attributes: true,
        attributeFilter: ['class'],
        subtree: true,
        childList: true,
        characterData: true
      });
    }

    const toolbar = quill.getModule('toolbar');
    if (!toolbar) return;

    // Add aria-label to header picker
    const headerPicker = toolbar.container.querySelector('.ql-header, .ql-picker');
    if (headerPicker) {
      const label = headerPicker.getElementsByClassName('ql-picker-label')[0];
      label?.setAttribute('aria-label', 'Header');

      const optionsContainer = headerPicker.getElementsByClassName('ql-picker-options')[0];
      const options = optionsContainer?.getElementsByClassName('ql-picker-item');
      if (options) {
        for (const item of options) {
          // Read the css 'content' values and generate aria labels
          const itemContent = window.getComputedStyle(item, ':before').content.replaceAll('"', '');
          item.setAttribute('aria-label', itemContent);
        }
      }
    }
  }

  setEnabled(enabled) {
    if (!this.quill) return

    this.quill.enable(enabled)
    this.textareaTarget.disabled = !enabled
    this.toolbarTarget.classList.toggle('pe-none', !enabled)
    this.element.classList.toggle('quill-disabled', !enabled)
  }

  disconnect() {
    this.tooltipLinkObserver?.disconnect();

    const form = this.formElement()
    if (form && this.submitHandler) {
      form.removeEventListener('submit', this.submitHandler)
    }
  }

  formElement() {
    if (this.hasFormTarget) return this.formTarget
    return this.textareaTarget?.form
  }

}
